import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {Sparkles, ChevronRight} from 'lucide-react';
import {useTheme} from 'styled-components';
import {useReservation} from 'context/reservation';
import {usePaymentSettings, useFetchOffers, usePath} from 'hooks';
import {PATHS} from 'routes/constants';
import {usePostHogTrack, POSTHOG_EVENTS} from 'analytics';
import {formatPrice} from '../price';
import type {GuruWalkTour} from 'hooks/useFetchGuruWalkTours';
import {
  Container,
  List,
  StyledOfferCard,
  HeaderContainer,
  HederText,
  Title,
  Description,
  ArrowButton,
} from './styled';

const GURUWALK_AFFILIATE = import.meta.env.VITE_GURUWALK_AFFILIATE;
const GURUWALK_ORIGIN = 'https://www.guruwalk.com';

const buildGuruWalkOfferUrl = ({
  tourPath,
  reservationId,
}: {
  tourPath: string;
  reservationId?: string;
}) => {
  const affiliate = GURUWALK_AFFILIATE || '';

  try {
    const url = new URL(tourPath, GURUWALK_ORIGIN);
    url.searchParams.set('ref', affiliate);

    if (reservationId) {
      url.searchParams.set('referrer_campaign', reservationId);
    }

    url.searchParams.set('utm_source', 'affiliates');
    url.searchParams.set('utm_medium', 'affiliates_widget');
    url.searchParams.set('utm_campaign', affiliate);

    return url.toString();
  } catch (_error) {
    return GURUWALK_ORIGIN;
  }
};

type Props = {
  guruWalkTours?: GuruWalkTour[];
};

export const HomeUpsellingList = ({guruWalkTours = []}: Props) => {
  const {t} = useTranslation();
  const {hasTemplate} = useTheme();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {data: reservation} = useReservation();
  const {data: paginatedOfferList, isLoading} = useFetchOffers({
    housingId: reservation?.housing_id,
  });
  const {currencySymbol} = usePaymentSettings();
  const {trackEvent} = usePostHogTrack();

  const hasOffers = (paginatedOfferList?.count ?? 0) > 0;
  const hasGuruWalk = guruWalkTours.length > 0;

  if ((!hasOffers && !hasGuruWalk) || isLoading) {
    return null;
  }

  return (
    <Container>
      <HeaderContainer>
        <div className="w-10 h-10 rounded-full flex items-center justify-center [background-color:var(--background-secondary)]">
          <Sparkles
            size={20}
            color={hasTemplate ? 'var(--brand-icons)' : 'var(--font-secondary)'}
          />
        </div>

        <HederText>
          <Title>{t('home_upselling_list.title')}</Title>
          <Description>{t('home_upselling_list.description')}</Description>
        </HederText>
        <ArrowButton
          onClick={() => {
            trackEvent(POSTHOG_EVENTS.RECOMMENDATIONS_CLICKED);
            navigate(getFullPath(PATHS.recommendations.main));
          }}
        >
          <ChevronRight
            size={20}
            color={hasTemplate ? 'var(--brand-icons)' : 'var(--font-secondary)'}
          />
        </ArrowButton>
      </HeaderContainer>
      <List>
        {paginatedOfferList?.results.map(
          ({title, id, category, price_type, items, highlight, image_url}) => (
            <StyledOfferCard
              key={id}
              id={id}
              title={title}
              price={formatPrice(currencySymbol)({priceType: price_type, items})}
              category={category?.toString()}
              imageUrl={image_url}
              highlight={highlight}
            />
          ),
        )}
        {guruWalkTours.map(tour => (
          <StyledOfferCard
            key={`guruwalk-${tour.id}`}
            id={tour.id}
            href={buildGuruWalkOfferUrl({
              tourPath: tour.url,
              reservationId: reservation?.id,
            })}
            title={tour.title}
            highlight={tour.description}
            imageUrl={tour.imageUrl ?? ''}
            price={t('offer_categories.free_offers')}
          />
        ))}
      </List>
    </Container>
  );
};
